import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useSelector, useDispatch } from "react-redux";
import { MinusIcon, PlusIcon, Trash2Icon, Check, X, Lock } from "lucide-react";
import { Badge } from "components/ui/badge";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { formatPrice, getProductPrice } from "utils/cartUtils";
import { getCurrencySymbol } from "utils/searchUtils";
import { formatDate } from "utils/helper";
import MouserModal from "./distributorModal/MouserModal";
import DigikeyModal from "./distributorModal/DigikeyModal";
import Element14Modal from "./distributorModal/Element14Modal";
import TMEModal from "./distributorModal/TMEModal";
import RutronikModal from "./distributorModal/RutronikModal";
import TIModal from "./distributorModal/TIModal";
import MailOrderModal from "./distributorModal/MailOrderModal";
import { mouserCreateCart, mouserOrderQuery } from "actions/mouserAction";
import {
  element14AddToCart,
  element14GetShippingAddress,
  element14GetShippingMethods,
} from "actions/element14Action";
import { fetchElement14AuthToken, runElement14Step } from "utils/element14Retry";
import { useAuth } from "AuthContext";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import SamiSpin from "components/SamiSpin";

const MySwal = withReactContent(Swal);

const DIST_META = {
  Mouser: { name: "Mouser", short: "Mou", badge: "bg-[#003478]" },
  Digikey: { name: "Digi-Key", short: "DK", badge: "bg-[#CC0000]" },
  Element14: { name: "Element 14", short: "E14", badge: "bg-[#FF6900]" },
  TME: { name: "TME", short: "TME", badge: "bg-[#1F8A5B]" },
  Rutronik: { name: "Rutronik", short: "Rut", badge: "bg-[#00509E]" },
  TI: { name: "Texas Instruments", short: "TI", badge: "bg-[#C8102E]" },
  Arrow: { name: "Arrow Electronics", short: "Arr", badge: "bg-[#1B1B1B]" },
  TTI: { name: "TTI", short: "TTI", badge: "bg-[#0F6CBD]" },
  Avnet: { name: "Avnet", short: "Avn", badge: "bg-[#5F259F]" },
  Verical: { name: "Verical", short: "Ver", badge: "bg-[#2E7D32]" },
};

// Distributors with no order-placement API and no dedicated modal (TME and
// Rutronik each have their own) — checkout goes through the shared
// MailOrderModal (email draft + Excel attachment) instead.
const MAIL_ORDER_DISTRIBUTOR_TYPES = ["Arrow", "TTI", "Avnet", "Verical"];

const CartTableComponent = ({
  type = "Mouser",
  cartItems = [],
  handleQtyChange,
  handleManualQtyChange,
  handleCartDelete,
}) => {
  const {
    addCartDetailItems,
    setCartDetailItems,
    cartDetailItems,
    cart,
    setCart,
    clearCartByPartner,
  } = useAuth();
  console.log("cartDetailItems=>>>>>>>>>>>>", { cartDetailItems, cart });
  const [isLoading, setIsLoading] = useState(false);
  const [modelData, setModelData] = useState({
    distributor: type,
    open: false,
    shipping: null,
    payment: {},
    shippingOption: [],
    paymentOption: [{ CreditCardLastFourDigits: null, Method: "", Name: "NET Terms" }],
    shippingAddress: [{ Code: "", Method: "UPS Worldwide Express Saver", Rate: "" }],
    orderQueryRes: null,
    CartKey: null,
    isLoading: false,
    shippingMethodOptions: [],
    selectedShippingMethod: null,
    paymentData: null,
  });

  const prices = cartItems.map((item) => item.required_qty * getProductPrice(item)); // Extract prices
  const totalPrice = prices.reduce((sum, price) => sum + price, 0);
  const reduxState = useSelector((state) => state.cart);
  console.log({ reduxState });

  const mouserCheckout = async () => {
    console.log("Mouser checkout");
    console.log("Proceed to checkout");

    try {
      setIsLoading(true);
      const mouserCartItems = cartItems.map((item) => {
        return {
          MouserPartNumber: item.api_partner_part_number,
          CustomerPartNumber: item.part_number,
          PackagingChoice: "None",
          Quantity: item.required_qty,
        };
      });

      const CartKey = uuidv4();
      const payload = {
        CartKey,
        MouserPaysCustomsAndDuties: false,
        CartItems: mouserCartItems,
      };

      console.log({ payload });

      const res = await mouserCreateCart({ payload });

      console.log({ res });

      if (res.status) addCartDetailItems(res.data);

      const orderQueryPayload = {
        OrderInitialize: {
          CartKey,
          CurrencyCode: "USD",
        },
      };

      console.log({ orderQueryPayload });

      // To get the shipping address, payment, methods
      const orderQueryRes = await mouserOrderQuery({
        payload: orderQueryPayload,
      });

      console.log({ orderQueryRes });

      if (orderQueryRes?.status) {
        setModelData({
          ...modelData,
          open: true,
          shippingOption: orderQueryRes.data.OrderInitialize?.Shipping?.Methods,
          paymentOption: orderQueryRes.data.OrderInitialize?.Payment?.PaymentTypes,
          shippingAddress: orderQueryRes.data.OrderInitialize?.ShippingAddress,
          orderQueryRes: orderQueryRes,
          paymentData: orderQueryRes.data.OrderInitialize?.Payment,
          CartKey: CartKey,
        });
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const digikeyCheckout = async () => {
    console.log("Digikey checkout");

    // Check if any item has required_qty > availability
    const itemsWithBackOrder = cartItems.filter((item) => item.required_qty > item.availability);

    if (itemsWithBackOrder.length > 0) {
      // Build message showing which items have backOrderQty
      const backOrderItemsList = itemsWithBackOrder
        .map((item) => {
          const backOrderQty = item.required_qty - item.availability;
          return `${item.part_number} (Back Order: ${backOrderQty})`;
        })
        .join(", ");

      const { isConfirmed } = await MySwal.fire({
        title: <strong>Back Order Items Detected</strong>,
        html: (
          <div>
            <p style={{ marginBottom: "10px" }}>
              The following items have quantities greater than availability:
            </p>
            <p style={{ marginBottom: "10px", fontWeight: "bold" }}>{backOrderItemsList}</p>
            <p>Do you want to proceed with the back order quantities?</p>
          </div>
        ),
        icon: "warning",
        cancelButtonText: "No",
        confirmButtonText: "Yes, Proceed",
        showCancelButton: true,
        confirmButtonColor: "#3b82f6",
        cancelButtonColor: "#ef4444",
      });

      if (!isConfirmed) {
        // User clicked "No" - handle items with backOrder quantity
        // Will remove the items with backOrder quantity from the cart
        const updatedCart = cartItems.filter((item) =>
          itemsWithBackOrder.find((data) => data.part_number !== item.part_number),
        );

        // Update cart in state and localStorage
        setCart(updatedCart);
        localStorage.setItem("cart", JSON.stringify(updatedCart));
        // Continue to proceed with checkout after removing backOrderQty
      }
    }
    console.log("Proceed with checkout");

    // Proceed with checkout (either no backOrder items, user confirmed, or backOrderQty removed)
    setModelData({ ...modelData, open: true });
  };

  const element14Checkout = async () => {
    try {
      setIsLoading(true);

      // Initial auth token (with Retry dialog if the token API fails).
      const tokenResult = await fetchElement14AuthToken();
      if (!tokenResult.ok) return;
      let authToken = tokenResult.token;

      const orderItem = cartItems.map((item) => ({
        offerId: item?.offer_id || item?.offerId || "0",
        partNumber: item.api_partner_part_number,
        quantity: item.required_qty,
      }));

      // Add to cart. On failure the Retry button re-fetches the token and
      // sends the fresh token into this same call again.
      const cartStep = await runElement14Step({
        token: authToken,
        title: "Add to Cart Failed",
        fallbackMessage: "Could not add items to Element14 cart.",
        apiCall: (t) => element14AddToCart({ orderItem, element_14_auth_token: t }),
      });
      if (!cartStep.ok) return;
      authToken = cartStep.token;
      const cartRes = cartStep.res;

      const dbOrderId = cartRes?.data?.orderId;
      const element14OrderId = cartRes?.data?.element14OrderId;

      // Shipping addresses (retry re-fetches the token, then this call).
      const addressStep = await runElement14Step({
        token: authToken,
        title: "Element14 Fetch Failed",
        fallbackMessage: "Could not fetch shipping addresses.",
        apiCall: (t) => element14GetShippingAddress({ element_14_auth_token: t }),
      });
      if (!addressStep.ok) return;
      authToken = addressStep.token;
      const addressRes = addressStep.res;

      // Shipping methods (retry re-fetches the token, then this call).
      const methodsStep = await runElement14Step({
        token: authToken,
        title: "Element14 Fetch Failed",
        fallbackMessage: "Could not fetch shipping methods.",
        apiCall: (t) => element14GetShippingMethods({ element_14_auth_token: t }),
      });
      if (!methodsStep.ok) return;
      authToken = methodsStep.token;
      const methodsRes = methodsStep.res;

      const shippingAddresses =
        addressRes?.data?.shippingAddresses ||
        addressRes?.data?.addresses ||
        (Array.isArray(addressRes?.data) ? addressRes.data : []);
      const shippingMethods =
        methodsRes?.data?.shippingMethods ||
        methodsRes?.data?.methods ||
        (Array.isArray(methodsRes?.data) ? methodsRes.data : []);

      setModelData({
        ...modelData,
        open: true,
        element_14_auth_token: authToken,
        dbOrderId,
        element14OrderId,
        shippingAddresses,
        shippingMethods,
        cartDetailItems: cartItems,
      });
    } catch (error) {
      console.log({ error });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceedToCheckout = async () => {
    console.log("handleProceedToCheckout", { type });
    if (type === "Mouser") await mouserCheckout();
    else if (type === "Digikey") await digikeyCheckout();
    else if (type === "Element14") await element14Checkout();
    else if (type === "TME") setModelData({ ...modelData, distributor: "TME", open: true });
    else if (type === "Rutronik")
      setModelData({ ...modelData, distributor: "Rutronik", open: true });
    else if (type === "TI") setModelData({ ...modelData, distributor: "TI", open: true });
    else if (MAIL_ORDER_DISTRIBUTOR_TYPES.includes(type)) setModelData({ ...modelData, distributor: type, open: true });
  };

  const handleRemoveAllByType = async () => {
    const { isConfirmed } = await MySwal.fire({
      title: <strong>Remove All {type} Items!</strong>,
      html: (
        <i>
          Are you sure you want to remove all {type} items from the cart? Other items will remain.
        </i>
      ),
      icon: "warning",
      cancelButtonText: "Cancel",
      confirmButtonText: "Yes, Remove All",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
    });

    if (isConfirmed) {
      clearCartByPartner(type);
    }
  };

  const meta = DIST_META[type] || { name: type, short: type.slice(0, 3), badge: "bg-s360-violet" };
  const totalUnits = cartItems.reduce(
    (acc, it) => acc + (parseInt(it.required_qty, 10) || 0),
    0,
  );

  return (
    <>
      <section className="bg-white rounded-md shadow-sm flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-[18px] py-3.5 border-b border-s360-stroke1 flex items-center gap-3 flex-wrap bg-gradient-to-b from-[#FBFAFF] to-white">
          <span
            className={`w-9 h-9 rounded-md inline-flex items-center justify-center text-[13px] font-bold text-white shrink-0 ${meta.badge}`}
          >
            {meta.short}
          </span>
          <div className="flex flex-col min-w-0">
            <h3 className="text-[18px] font-bold text-s360-ink">{meta.name}</h3>
            <span className="text-xs text-s360-fg3">
              {cartItems.length} part{cartItems.length !== 1 ? "s" : ""} ·{" "}
              {totalUnits.toLocaleString()} units
            </span>
          </div>
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            <div className="flex flex-col items-end">
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-s360-fg3">
                Subtotal
              </span>
              <span className="font-mono text-[18px] font-bold text-s360-ink">
                {getCurrencySymbol(cartItems[0]?.price?.currency)} {totalPrice.toFixed(2)}
              </span>
            </div>
            <button
              onClick={handleRemoveAllByType}
              disabled={cartItems.length === 0}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-[#F1BBC0] text-[13px] font-semibold text-s360-danger hover:bg-s360-danger-bg disabled:opacity-60"
            >
              Remove all
            </button>
            <button
              onClick={handleProceedToCheckout}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-s360-violet text-white text-[13px] font-semibold hover:bg-s360-violet-600 disabled:opacity-60"
            >
              {isLoading ? <SamiSpin className="w-4 h-4 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
              Checkout {meta.name}
            </button>
          </div>
        </div>

        {/* Table Container */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                {[
                  { label: "Part Number", cls: "text-left" },
                  { label: "Manufacturer", cls: "text-left" },
                  { label: "Available Qty", cls: "text-left" },
                  { label: "Back Order", cls: "text-right" },
                  { label: "Quantity", cls: "text-center" },
                  { label: "Unit Price", cls: "text-right" },
                  { label: "Total Price", cls: "text-right" },
                  { label: "Added On", cls: "text-left" },
                  { label: "Actions", cls: "text-center" },
                ].map((c) => (
                  <th
                    key={c.label}
                    className={`px-3.5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap ${c.cls}`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {cartItems.map((item, index) => {
                const price = getProductPrice(item);
                const inStock = item.availability > 0;
                return (
                  <tr key={index} className="border-b border-s360-bg2 last:border-0 hover:bg-s360-stripe">
                    <td className="px-4 py-3">
                      <div className="font-mono text-sm font-semibold text-s360-fg1">
                        {item.part_number}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-[13px] text-s360-fg2">{item.manufacturer}</td>

                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                          inStock ? "bg-s360-success-bg text-s360-success-ink" : "bg-s360-danger-bg text-[#700A14]"
                        }`}
                      >
                        {inStock ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        {Number(item.availability).toLocaleString()}
                        <span className="font-medium opacity-80">{inStock ? "in stock" : "out"}</span>
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right text-[13px]">
                      {item.required_qty > item.availability ? (
                        <span className="font-mono font-semibold text-s360-danger">
                          {Math.abs(item.required_qty - item.availability).toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-s360-fg-disabled">—</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center">
                        <div className="inline-flex items-center border border-s360-stroke1 rounded-md overflow-hidden">
                          <button
                            type="button"
                            className="px-2 py-1.5 hover:bg-s360-bg2 text-s360-fg2"
                            onClick={() => handleQtyChange(index, "decrease", item)}
                            aria-label="Decrease quantity"
                          >
                            <MinusIcon className="h-3.5 w-3.5" />
                          </button>
                          <input
                            type="text"
                            onChange={(e) =>
                              handleManualQtyChange({ value: e.target.value, item, index })
                            }
                            value={item?.required_qty}
                            className="w-14 text-center font-semibold border-x border-s360-stroke1 py-1.5 focus:outline-none text-sm tabular-nums"
                          />
                          <button
                            type="button"
                            className="px-2 py-1.5 hover:bg-s360-bg2 text-s360-fg2"
                            onClick={() => handleQtyChange(index, "increase", item)}
                            aria-label="Increase quantity"
                          >
                            <PlusIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right font-mono text-[13px] text-s360-fg1 tabular-nums">
                      {getCurrencySymbol(item.price.currency)} {price}
                    </td>

                    <td className="px-4 py-3 text-right font-mono text-[13px] font-bold text-s360-ink tabular-nums">
                      {getCurrencySymbol(item.price.currency)} {formatPrice(item.required_qty * price)}
                    </td>

                    <td className="px-4 py-3 text-xs text-s360-fg3 whitespace-nowrap">
                      {item.created_at ? formatDate(item.created_at) : "-"}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <button
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-[#F1BBC0] text-s360-danger hover:bg-s360-danger hover:text-white"
                        onClick={() => handleCartDelete(item)}
                        title="Remove"
                      >
                        <Trash2Icon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {cartItems.length > 0 && (
              <tfoot>
                <tr className="bg-s360-bg2 border-t border-s360-stroke1">
                  <td colSpan={5} className="px-4 py-3 text-[13px] text-s360-violet-900">
                    Total items: <span className="font-bold">{cartItems.length}</span>
                  </td>
                  <td colSpan={2} className="px-4 py-3 text-right text-[13px] font-semibold text-s360-violet-900">
                    Subtotal{" "}
                    <span className="font-mono font-bold text-s360-ink ml-2">
                      {getCurrencySymbol(cartItems[0]?.price?.currency)} {totalPrice.toFixed(2)}
                    </span>
                  </td>
                  <td colSpan={2} className="px-4 py-3 text-center">
                    <button
                      onClick={handleProceedToCheckout}
                      disabled={isLoading}
                      className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-s360-violet text-white text-[13px] font-semibold hover:bg-s360-violet-600 disabled:opacity-60"
                    >
                      {isLoading ? <SamiSpin className="w-4 h-4 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
                      Proceed to checkout
                    </button>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>
      {modelData.distributor === "Mouser" && modelData.open && (
        <MouserModal modelData={modelData} setModelData={setModelData} />
      )}
      {modelData.distributor === "Digikey" && modelData.open && (
        <DigikeyModal open={modelData.open} modelData={modelData} setModelData={setModelData} />
      )}
      {modelData.distributor === "Element14" && modelData.open && (
        <Element14Modal modelData={modelData} setModelData={setModelData} />
      )}
      {modelData.distributor === "TME" && modelData.open && (
        <TMEModal open={modelData.open} modelData={modelData} setModelData={setModelData} />
      )}
      {modelData.distributor === "Rutronik" && modelData.open && (
        <RutronikModal open={modelData.open} modelData={modelData} setModelData={setModelData} />
      )}
      {modelData.distributor === "TI" && modelData.open && (
        <TIModal open={modelData.open} modelData={modelData} setModelData={setModelData} />
      )}
      {MAIL_ORDER_DISTRIBUTOR_TYPES.includes(modelData.distributor) && modelData.open && (
        <MailOrderModal
          open={modelData.open}
          modelData={modelData}
          setModelData={setModelData}
          type={modelData.distributor}
        />
      )}
    </>
  );
};

export default CartTableComponent;
